import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, Package, CheckCircle2, Pause } from 'lucide-react';
import { useAdminAddons, type AdminAddon } from '@/hooks/useAdminCatalog';
import NewVersionDialog from './NewVersionDialog';

const BRAND_LABELS: Record<string, string> = {
  saint_gobain: 'Saint-Gobain',
  knauf: 'Knauf',
  fassa: 'Fassa Bortolo',
};

export default function AdminListini() {
  const { data, isLoading } = useAdminAddons();
  const [showNewVersion, setShowNewVersion] = useState(false);

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
  }
  if (!data) return null;

  // Solo i listini (category=brand), raggruppati per brand_slug
  const listini = data.filter((a) => a.category === 'brand');
  const byBrand = listini.reduce<Record<string, AdminAddon[]>>((acc, a) => {
    const k = a.brand_slug ?? 'altro';
    (acc[k] ??= []).push(a);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Listini per brand</h1>
          <p className="text-sm text-zinc-500">Ogni brand ha N versioni annuali. Solo la più recente è marcata "Corrente".</p>
        </div>
        <Button onClick={() => setShowNewVersion(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Nuova versione listino
        </Button>
      </div>
      <NewVersionDialog open={showNewVersion} onOpenChange={setShowNewVersion} allAddons={data} />

      {Object.entries(byBrand).map(([brand, versions]) => (
        <Card key={brand}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4 text-blue-600" />
              {BRAND_LABELS[brand] ?? brand}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {versions.sort((a, b) => (b.version_year ?? 0) - (a.version_year ?? 0)).map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded border border-zinc-200">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-semibold text-zinc-900 truncate">{v.name}</span>
                  <Badge variant="outline" className="text-[10px] bg-zinc-50">v{v.version_year ?? '?'}</Badge>
                  {v.is_current_year && (
                    <Badge className="text-[10px] bg-emerald-600 hover:bg-emerald-600">
                      <CheckCircle2 className="h-3 w-3 mr-0.5" />
                      Corrente
                    </Badge>
                  )}
                  {!v.is_active && (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                      <Pause className="h-3 w-3 mr-0.5" />
                      Inactive
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-zinc-500">{v.preview_count_materials.toLocaleString('it-IT')}+ materiali</span>
                  <span className="font-mono text-zinc-900">€{Number(v.price_eur).toFixed(0)}</span>
                  {v.upgrade_price_eur && (
                    <span className="font-mono text-emerald-700 text-[10px]">↑ €{Number(v.upgrade_price_eur).toFixed(0)} (upgrade)</span>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
